/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import EventEmitter from 'eventemitter3';

import StretchEnum from '../stretch/stretch-enum';

/**
 * @import { IElement } from '../div'
 * @import { EventTypes, ViewerController } from '../viewer/controller'
 * @import ScheduleInfo from '../schedule/schedule-info'
 * @import ViewerLifeCycleFactory from '../viewer/life-cycle/viewer-life-cycle-factory'
 */

/**
 * Ovladač pro zobrazování obrázkových souborů.
 * Namísto standardního zobrazování v `<img>`, provádí ovladač zobrazení
 * obrázků jako CSS background-image na obyčejném `<div>`.
 * Tento přístup byl zvolen, protože má lepší výkon.
 * U načítání `<img>` na starších zařízeních dochází k chvilkovému zaseknuní
 * vykreslování prohlížeče, což vadí u výkonu zobrazovaných médií.
 * Problém je viditelný především na animacích typu marqee.
 * Kvůli omezením CSS neumí ovladač detekovat dokončení načtení obrázku,
 * událost se vyvolá ihned po začátku načítání.
 * Také se neprovádí žádná detekce selhání při načítání obrázku.
 * @implements {ViewerController}
 */
export default class ImageViewerController {
  /**
   * Vytvoří instanci.
   * @param {IElement} el
   * @param {ViewerLifeCycleFactory} lifeCycleFactory
   */
  constructor(el, lifeCycleFactory) {
    this.el = el;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter<EventTypes>}
     */
    this.emitter = new EventEmitter();
    /** @type {ScheduleInfo | null} */
    this.scheduleInfo = null;
    this.viewerLifeCycle = lifeCycleFactory.create();
  }

  /** @type {ViewerController['destroy']} */
  destroy() {
    this.scheduleInfo?.releaseMaybeBlobUrl();
    // Prázdná metoda pro splnění rozhraní.
  }

  /** @type {ViewerController['enter']} */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      this.emitter.emit('playing');
    });
  }

  /**
   * Určí CSS třídu s roztažením, která se má přiřadit elementu.
   * @param {StretchEnum} stretchEnum
   * @private
   * @returns {string} Název CSS třídy.
   */
  getStretchClassName(stretchEnum) {
    switch (stretchEnum) {
      case StretchEnum.CONTAIN:
        return 'image-viewer-controller--stretch-contain';
      case StretchEnum.COVER:
        return 'image-viewer-controller--stretch-cover';
      case StretchEnum.FILL:
        return 'image-viewer-controller--stretch-fill';
      default:
        throw new Error(`Invalid stretch "${stretchEnum}".`);
    }
  }

  /** @type {ViewerController['leave']} */
  leave() {
    return this.viewerLifeCycle.leave(() => {});
  }

  /** @type {ViewerController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ViewerController['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /** @type {ViewerController['release']} */
  release() {
    return this.viewerLifeCycle.release(() => {
      this.scheduleInfo?.releaseMaybeBlobUrl();
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
    });
  }

  /** @type {ViewerController['seize']} */
  seize(scheduleInfo) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const stretchEnum = scheduleInfo.getStretchEnum();
        const { el } = this;
        const stretchClassName = this.getStretchClassName(stretchEnum);
        // Příprava zdrojového elementu.
        el.classList.add('image-viewer-controller', stretchClassName);
        // Nastavení zdroje.
        el.style.backgroundImage = `url("${url}")`;
        // U CSS pozadí nelze sledovat okamžik dokončení.
        // Událost se tedy vyvolá alespoň ihned,
        // i když možná není načtení provedeno.
        this.emitter.emit('loaded');
      });
    });
  }
}
